import React, { useCallback, type ReactNode } from 'react';
import SectionMessage from '@atlaskit/section-message';
import { Stack, Text } from '@atlaskit/primitives/compiled';
import Link from '@atlaskit/link';
import { Code, CodeBlock } from '@atlaskit/code';
import { useIntl } from '@atlassian/jira-intl';
import type { ApplicationLinkV3Status } from '../../../../common/entities.tsx';
import { lookupTypeByTypeId } from '../../../../common/application-types.tsx';
import type { ApplinksError } from '../../../../common/status/errors.tsx';
import messages from '../messages.tsx';

import AuthLevelMismatchDescription from './complex/AuthLevelMismatchDescription.tsx';

type ErrorStatusSectionMessageProps = {
	err: ApplinksError;
	context: ApplicationLinkV3Status;
};

type StatusAction = 'auth' | 'edit' | 'update';

type ErrorContents = {
	title: string;
	description: ReactNode;
	action?: StatusAction;
};

export const ErrorStatusSectionMessage = (props: ErrorStatusSectionMessageProps) => {
	const { formatMessage } = useIntl();

	const buildErrorContents = useCallback((): ErrorContents => {
		switch (props.err.type) {
			case 'CONNECTION_REFUSED':
				return {
					title: formatMessage(messages.connectionRefusedTitleNonfinal),
					description: formatMessage(messages.connectionRefusedDescriptionNonfinal, {
						rpcUrl: props.context.link.rpcUrl,
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
						code: (chunks: ReactNode[]) => <Code>{chunks}</Code>,
					}),
					action: 'edit',
				};
			case 'UNKNOWN_HOST':
				return {
					title: formatMessage(messages.unknownHostTitleNonfinal),
					description: formatMessage(messages.unknownHostDescriptionNonfinal, {
						rpcUrl: props.context.link.rpcUrl,
						code: (chunks: ReactNode[]) => <Code>{chunks}</Code>,
					}),
					action: 'edit',
				};
			case 'SSL_UNTRUSTED':
				return {
					title: formatMessage(messages.sslUntrustedTitleNonfinal),
					description: formatMessage(messages.sslUntrustedDescriptionNonfinal, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'SSL_HOSTNAME_UNMATCHED':
				return {
					title: formatMessage(messages.sslHostnameUnmatchedTitleNonfinal),
					description: formatMessage(messages.sslHostnameUnmatchedDescriptionNonfinal, {
						applicationName: props.context.link.name,
						rpcUrl: <Code>{props.context.link.rpcUrl}</Code>,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'SSL_UNMATCHED':
				return {
					title: formatMessage(messages.sslUnmatchedTitleNonfinal),
					description: formatMessage(messages.sslUnmatchedDescriptionNonfinal, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'OAUTH_PROBLEM':
				return {
					title: formatMessage(messages.oauthProblemTitleNonfinal),
					description: formatMessage(messages.oauthProblemDescriptionNonfinal, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'OAUTH_TIMESTAMP_REFUSED':
				return {
					title: formatMessage(messages.oauthTimestampRefusedTitleNonfinal),
					description: formatMessage(messages.oauthTimestampRefusedDescriptionNonfinal, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'OAUTH_SIGNATURE_INVALID':
				return {
					title: formatMessage(messages.oauthSignatureInvalidTitleNonfinal),
					description: formatMessage(messages.oauthSignatureInvalidDescriptionNonfinal, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'UNEXPECTED_RESPONSE':
				return {
					title: formatMessage(messages.unexpectedResponseTitleNonfinal),
					description: formatMessage(messages.unexpectedResponseDescriptionNonfinal, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'UNEXPECTED_RESPONSE_STATUS':
				return {
					title: formatMessage(messages.unexpectedResponseTitleNonfinal),
					description: (
						<Stack space="space.100">
							<Text>
								{formatMessage(messages.unexpectedResponseDescriptionNonfinal, {
									applicationName: props.context.link.name,
									link: (chunks: ReactNode[]) => (
										<Link href={props.context.link.displayUrl} target="_blank">
											{chunks}
										</Link>
									),
								})}
							</Text>
							{props.context.error && props.context.error.details && (
								<CodeBlock text={props.context.error.details} />
							)}
						</Stack>
					),
				};
			case 'RECREATION_NEEDED':
				return {
					title: formatMessage(messages.recreationNeededTitleNonfinal),
					description: formatMessage(messages.recreationNeededDescriptionNonfinal),
				};
			case 'PARTIALLY_CONFIGURED':
				return {
					title: formatMessage(messages.partiallyConfiguredTitleNonfinal),
					description: formatMessage(messages.partiallyConfiguredDescriptionNonfinal),
				};
			case 'SETTING_UP':
				return {
					title: formatMessage(messages.settingUpTitleNonfinal),
					description: formatMessage(messages.settingUpDescriptionNonfinal),
				};
			case 'RESUBMISSION_REQUIRED':
				return {
					title: formatMessage(messages.resubmissionRequiredTitleNonfinal),
					description: formatMessage(messages.resubmissionRequiredDescriptionNonfinal),
				};
			case 'PARTIALLY_CONNECTED':
				return {
					title: formatMessage(messages.partiallyConnectedTitleNonfinal),
					description: formatMessage(messages.partiallyConnectedDescriptionNonfinal),
				};
			case 'AUTH_LEVEL_MISMATCH': {
				return {
					title: formatMessage(messages.authLevelMismatchTitleNonfinal),
					description: <AuthLevelMismatchDescription status={props.context} />,
					action: 'edit',
				};
			}
			case 'NO_REMOTE_APPLINK':
				return {
					title: formatMessage(messages.noRemoteApplinkTitleNonfinal),
					description: formatMessage(messages.noRemoteApplinkDescriptionNonfinal, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'INSUFFICIENT_REMOTE_PERMISSION':
			case 'REMOTE_VERSION_INCOMPATIBLE':
				return {
					title: formatMessage(messages.remoteVersionIncompatibleTitleNonfinal),
					description: formatMessage(messages.remoteVersionIncompatibleDescriptionNonfinal, {
						applicationName: props.context.link.name,
						// TODO: Try determine whether the link is DC or not (it doesn't strictly matter, as it'll still say "Confluence", etc)
						applicationType: lookupTypeByTypeId(props.context.link.type, false).name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'NO_OUTGOING_AUTH':
				return {
					title: formatMessage(messages.noOutgoingAuthTitleNonfinal),
					description: formatMessage(messages.noOutgoingAuthDescriptionNonfinal),
				};
			case 'GENERIC_LINK':
				return {
					title: formatMessage(messages.genericLinkTitleNonfinal),
					description: formatMessage(messages.genericLinkDescriptionNonfinal),
				};
			case 'NON_ATLASSIAN':
				return {
					title: formatMessage(messages.nonAtlassianTitleNonfinal),
					description: formatMessage(messages.nonAtlassianDescriptionNonfinal),
				};
			case 'SYSTEM_LINK':
				return {
					title: formatMessage(messages.systemLinkTitleNonfinal),
					description: formatMessage(messages.systemLinkDescriptionNonfinal),
				};
			case 'AUTH_LEVEL_UNSUPPORTED':
				return {
					title: formatMessage(messages.authLevelUnsupportedTitleNonfinal),
					description: formatMessage(messages.authLevelUnsupportedDescriptionNonfinal),
					action: 'edit',
				};
			case 'LEGACY_UPDATE':
				return {
					title: formatMessage(messages.legacyUpdateTitleNonfinal),
					description: (
						<Stack space="space.100">
							<Text>{formatMessage(messages.legacyUpdateDescription1Nonfinal)}</Text>
							<Text>{formatMessage(messages.legacyUpdateDescription2Nonfinal)}</Text>
						</Stack>
					),
					action: 'update',
				};
			case 'LEGACY_REMOVAL':
				return {
					title: formatMessage(messages.legacyRemovalTitleNonfinal),
					description: (
						<Stack space="space.100">
							<Text>{formatMessage(messages.legacyRemovalDescription1Nonfinal)}</Text>
							<Text>{formatMessage(messages.legacyRemovalDescription2Nonfinal)}</Text>
						</Stack>
					),
					action: 'update',
				};
			case 'MANUAL_LEGACY_UPDATE':
				return {
					title: formatMessage(messages.manualLegacyUpdateTitleNonfinal),
					description: (
						<Stack space="space.100">
							<Text>{formatMessage(messages.manualLegacyUpdateDescription1Nonfinal)}</Text>
							<Text>{formatMessage(messages.manualLegacyUpdateDescription2Nonfinal)}</Text>
						</Stack>
					),
				};
			case 'MANUAL_LEGACY_REMOVAL':
				return {
					title: formatMessage(messages.manualLegacyRemovalTitleNonfinal),
					description: (
						<Stack space="space.100">
							<Text>{formatMessage(messages.manualLegacyRemovalDescription1Nonfinal)}</Text>
							<Text>
								{formatMessage(messages.manualLegacyRemovalDescription2Nonfinal, {
									bold: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
								})}
							</Text>
							<Text>
								{formatMessage(messages.manualLegacyRemovalDescription3Nonfinal, {
									applicationName: props.context.link.name,
									link: (chunks: ReactNode[]) => (
										<Link href={props.context.link.displayUrl} target="_blank">
											{chunks}
										</Link>
									),
								})}
							</Text>
						</Stack>
					),
					action: 'update',
				};
			case 'UNKNOWN':
			default:
				return {
					title: formatMessage(messages.unknownTitleNonfinal),
					description: formatMessage(messages.unknownDescriptionNonfinal, {
						applicationName: props.context.link.name,
						rpcUrl: <Code>{props.context.link.rpcUrl}</Code>,
					}),
					action: 'edit',
				};
		}
	}, [props.err, props.context, formatMessage]);

	const buildActions = useCallback(() => {
		switch (buildErrorContents().action) {
			case 'edit': {
				// We don't show edit actions on the edit page
				// However when this class is extended to support popups on the list page, we'll show it there
				return [];
			}
			case 'update': {
				return [];
				// return [
				// 	<SectionMessageAction key="action-update" href="#">
				// 		{formatMessage(messages.actionUpdateNonfinal)}
				// 	</SectionMessageAction>,
				// ];
			}
			default:
				return [];
		}
	}, [buildErrorContents]);

	return (
		<SectionMessage appearance="error" title={buildErrorContents().title} actions={buildActions()}>
			{buildErrorContents().description}
		</SectionMessage>
	);
};

export default ErrorStatusSectionMessage;
